import React, { useEffect } from "react";
import { Box, Tabs, TabList, Tab, TabPanels, TabPanel, useBreakpointValue } from "@chakra-ui/react";
import ConcertInfo from "./ConcertInfo";
import NfiRoad from "./NfiRoad";
import NoData from "./NoData";
import { useTranslation } from "react-i18next";

interface ConcertDate {
  date: string;
  start_time: string;
  duration_minutes: number;
}

interface TicketOpen {
  date: string;
  time: string;
}

interface Concert {
  id: number;
  name: string;
  location: string;
  startTime: string;
  concertDate: ConcertDate[];
  type: string;
  performanceType: string;
  artists: string[];
  poster: string;
  EventState: number;
  ticketOpen: TicketOpen;
  ticketLink: string;
  lat: number;
  lng: number;
  globals: boolean;
  isTicketOpenDate: boolean;
}

type NfiRoadType = {
  id: number;
  name: string;
  location: string;
  category: string;
  lat: string;
  lng: string;
  naverLink: string;
  note: string;
};

type SidebarProps = {
  concerts: Concert[];
  globalConcerts: Concert[];
  nfiRoad: NfiRoadType[];
  query: string;
  setQuery: (query: string) => void;
  globalQuery: string;
  setGlobalQuery: (query: string) => void;
  showPastConcerts: boolean;
  setShowPastConcerts: (show: boolean) => void;
  setSelectedConcert: (concert: Concert) => void;
  selectedConcert: Concert | null;
  setSelectedNfiRoad: (nfiRoad: NfiRoadType) => void;
  selectedNfiRoad: NfiRoadType | null;
  activeTabIndex: number;
  setActiveTabIndex: (index: number) => void;
  showPastConcertsGlobal: boolean;
  setShowPastConcertsGlobal: (show: boolean) => void;
  setSelectedGlobalConcert: (concert: Concert) => void;
  selectedGlobalConcert: Concert | null;
  years: string[];
  selectedYear: string;
  setSelectedYear: (year: string) => void;
  selectedGlobalYear: string;
  setSelectedGlobalYear: (year: string) => void;
};

const Sidebar = ({
  concerts,
  nfiRoad,
  globalConcerts,
  query,
  setQuery,
  showPastConcerts,
  setShowPastConcerts,
  setSelectedConcert,
  selectedConcert,
  setSelectedNfiRoad,
  selectedNfiRoad,
  activeTabIndex,
  setActiveTabIndex,
  showPastConcertsGlobal,
  setShowPastConcertsGlobal,
  setSelectedGlobalConcert,
  selectedGlobalConcert,
  globalQuery,
  setGlobalQuery,
  years,
  selectedYear,
  setSelectedYear,
  selectedGlobalYear,
  setSelectedGlobalYear,
}: SidebarProps) => {
  const { t, i18n } = useTranslation();
  const isMobileOrTablet = useBreakpointValue({ base: true, md: true, lg: false });

  return (
    <Box
      w="340px"
      bg="#fff"
      h={isMobileOrTablet ? "calc(100vh - 120px)" : "calc(100svh - 70px)"}
      boxShadow="soft"
      borderRight="1px solid"
      borderColor="gray.100"
      display="flex"
      flexDirection="column"
      zIndex={10}
    >
      <Tabs
        index={activeTabIndex}
        onChange={setActiveTabIndex}
        display="flex"
        flexDirection="column"
        height="100%"
        variant="unstyled"
      >
        <TabList borderBottom="1px solid" borderColor="gray.100" p={2} gap={1}>
          <Tab
            fontSize="sm"
            fontWeight="extrabold"
            textAlign="center"
            borderRadius="full"
            color="gray.500"
            _selected={{ bg: "brand.purpleSoft", color: "brand.main" }}
            _focus={{ boxShadow: "none" }}
            flex="1"
            py={2.5}
            transition="all 0.2s"
          >
            {t("map_domestic")}
          </Tab>
          <Tab
            fontSize="sm"
            fontWeight="extrabold"
            textAlign="center"
            borderRadius="full"
            color="gray.500"
            _selected={{ bg: "brand.purpleSoft", color: "brand.main" }}
            _focus={{ boxShadow: "none" }}
            flex="1"
            py={2.5}
            transition="all 0.2s"
          >
            {t("map_global")}
          </Tab>
          <Tab
            fontSize="sm"
            fontWeight="extrabold"
            textAlign="center"
            borderRadius="full"
            color="gray.500"
            _selected={{ bg: "brand.purpleSoft", color: "brand.main" }}
            _focus={{ boxShadow: "none" }}
            flex="1"
            py={2.5}
            transition="all 0.2s"
          >
            {t("map_nfiRoad")}
          </Tab>
        </TabList>

        <TabPanels flex="1" overflowY="hidden">
          <TabPanel
            key={`domestic-${activeTabIndex}`}
            height="100%"
            p={4}
          >
            <ConcertInfo
              concerts={concerts}
              query={query}
              setQuery={setQuery}
              showPastConcerts={showPastConcerts}
              setShowPastConcerts={setShowPastConcerts}
              setSelectedConcert={setSelectedConcert}
              years={years}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
            />
          </TabPanel>
          <TabPanel
            key={`global-${activeTabIndex}`}
            height="100%"
            p={4}
          >
            <ConcertInfo
              concerts={globalConcerts}
              query={globalQuery}
              setQuery={setGlobalQuery}
              showPastConcerts={showPastConcertsGlobal}
              setShowPastConcerts={setShowPastConcertsGlobal}
              setSelectedConcert={setSelectedGlobalConcert}
              years={years}
              selectedYear={selectedGlobalYear}
              setSelectedYear={setSelectedGlobalYear}
            />
          </TabPanel>
          <TabPanel
            key={`nfiRoad-${activeTabIndex}`}
            height="100%"
            p={4}
          >
            <NfiRoad
              nfiRoad={nfiRoad}
              setSelectedNfiRoad={setSelectedNfiRoad}
              selectedNfiRoad={selectedNfiRoad}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default Sidebar;
